import React, { useState, useEffect, useRef } from "react";
import { useTranslate } from "./TranslationText";
import { useLocation } from "react-router-dom";
import axiosInstance from "../axios/axios";

const DISMISS_KEY = "pollDismissedAt";
const DISMISS_DURATION_MS = 30 * 60 * 1000; // 30 minutes

export function normalizeSurveyData(payload) {
  if (!payload || typeof payload !== "object") return null;

  const result = payload?.result;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }

  if (result && typeof result === "object" && Object.keys(result).length > 0) {
    return result;
  }

  const data = payload?.data;
  if (data && typeof data === "object" && Object.keys(data).length > 0) {
    return data;
  }

  if (Object.keys(payload).length > 0) {
    return payload;
  }

  return null;
}

export default function PollPopup() {
  const { translate } = useTranslate();
  const [visible, setVisible] = useState(false);
  const [surveyData, setSurveyData] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    Boolean(localStorage.getItem("accessTokenforECUP")),
  );

  const location = useLocation();
  const surveyDataRef = useRef(null);

  useEffect(() => {
    setIsLoggedIn(Boolean(localStorage.getItem("accessTokenforECUP")));
  }, [location.pathname]);

  useEffect(() => {
    let showTimer;
    let delayTimer;

    if (!isLoggedIn) return;
    if (visible) return;

    async function fetchAndShow() {
      try {
        if (surveyDataRef.current) {
          setVisible(true);
          return;
        }

        let response;
        try {
          response = await axiosInstance.get(
            "/api/SurveyAccess/GetLatestSurvey",
          );
        } catch (firstError) {
          response = await axiosInstance.get("/SurveyAccess/GetLatestSurvey");
        }

        const normalizedSurvey = normalizeSurveyData(response?.data);

        if (normalizedSurvey) {
          surveyDataRef.current = normalizedSurvey;
          setSurveyData(normalizedSurvey);
          setVisible(true);
        } else {
          setSurveyData(null);
          setVisible(false);
        }
      } catch (e) {
        console.error("Failed to fetch survey", e);
      }
    }

    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      if (elapsed < DISMISS_DURATION_MS) {
        const remaining = DISMISS_DURATION_MS - elapsed;
        delayTimer = setTimeout(fetchAndShow, remaining);
      } else {
        fetchAndShow();
      }
    } else {
      fetchAndShow();
    }

    return () => {
      clearTimeout(showTimer);
      clearTimeout(delayTimer);
    };
  }, [location.pathname, visible, isLoggedIn]);

  const handleTakeSurvey = () => {
    if (surveyData?.url) {
      window.open(surveyData.url, "_blank", "noopener,noreferrer");
    }
    // Remove dismiss timer so it doesn't re-appear after survey is taken
    localStorage.removeItem(DISMISS_KEY);
    setVisible(false);
  };

  const handleDismiss = () => {
    // Save current time — popup won't show again for 30 min
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setVisible(false);
  };

  if (!visible || !surveyData) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 9998,
          animation: "pollFadeIn 0.3s ease",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "#fff",
          borderRadius: 16,
          padding: "40px 36px",
          zIndex: 9999,
          maxWidth: 460,
          width: "90%",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          animation: "pollSlideIn 0.4s ease",
        }}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          style={{
            position: "absolute",
            top: 12,
            right: 16,
            background: "none",
            border: "none",
            fontSize: 22,
            color: "#999",
            cursor: "pointer",
          }}
        >
          <i className="fa-solid fa-xmark"></i>
        </button>

        {/* Icon */}
        <div
          style={{
            width: 70,
            height: 70,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #219653, #27AE60)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          <i
            className="fa-solid fa-clipboard-list"
            style={{ fontSize: 30, color: "#fff" }}
          ></i>
        </div>

        {/* Title from API */}
        <h4 className="fw-bold mb-2" style={{ color: "#222" }}>
          {translate("web.pollTitle", "We Value Your Opinion!")}
        </h4>

        {/* Subtitle from API */}
        <p
          className="text-muted mb-4"
          style={{ fontSize: 14, lineHeight: 1.6 }}
        >
          {translate(
            "web.pollDescription",
            "Help us improve! Take a quick survey about {0} and share your feedback.",
          ).replace("{0}", surveyData?.title || "")}
        </p>

        <div className="d-flex gap-3 justify-content-center">
          {/* Take Survey — opens URL from API in new tab */}
          <button
            className="btn px-4 py-2 fw-bold"
            style={{
              backgroundColor: "#219653",
              color: "#fff",
              borderRadius: 8,
              border: "none",
              fontSize: 14,
              transition: "all 0.2s",
            }}
            onClick={handleTakeSurvey}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#1B7D3B")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#219653")
            }
          >
            <i className="fa-solid fa-pen-to-square me-2"></i>
            {translate("web.takeSurvey", "Take Survey")}
          </button>

          {/* Maybe Later — saves timestamp, shows again after 10 min */}
          <button
            className="btn px-4 py-2"
            style={{
              backgroundColor: "#f5f5f5",
              color: "#666",
              borderRadius: 8,
              border: "1px solid #ddd",
              fontSize: 14,
              transition: "all 0.2s",
            }}
            onClick={handleDismiss}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#eee")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#f5f5f5")
            }
          >
            {translate("web.maybeLater", "Maybe Later")}
          </button>
        </div>
      </div>

      <style>{`
                @keyframes pollFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes pollSlideIn {
                    from { opacity: 0; transform: translate(-50%, -45%); }
                    to { opacity: 1; transform: translate(-50%, -50%); }
                }
            `}</style>
    </>
  );
}
